import { useCallback, useRef, useState } from "react";
import { useAutoWrittenValue } from "../../../../hooks";
import { toIsoDate } from "../../../../utils/dates";
import { isBlank } from "../../../../utils/taxWithheld";
import {
  CashbackPrefillRequest,
  CashbackPrefillValue,
  PendingCashbackPrefill,
  UseCashbackPrefillParams,
} from "./types";

export const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// The date input writes a Date once the user types a day; the form default is still an ISO string.
export const isoDateOf = (value: string | Date | null | undefined): string =>
  value instanceof Date ? toIsoDate(value) : (value ?? "");

const field = "cashbackEarned";

export const useCashbackPrefill = ({
  form: { getValues, setValue },
  action,
}: UseCashbackPrefillParams) => {
  const [pending, setPending] = useState<PendingCashbackPrefill | null>(null);
  const [error, setError] = useState("");
  const initial = useRef<{ key?: string; changed: boolean }>({
    changed: false,
  });
  const { markWritten, isUserValue } =
    useAutoWrittenValue<CashbackPrefillValue | null>(getValues(field));

  // An unchanged edit keeps the recorded value, even when cashback has since been disabled.
  const isUnchangedEdit = useCallback(
    (key: string) => {
      initial.current.key ??= key;
      if (initial.current.key !== key) {
        initial.current.changed = true;
      }
      return action === "edit" && !initial.current.changed;
    },
    [action],
  );

  const clearError = useCallback(() => setError(""), []);

  const write = useCallback(
    (value: CashbackPrefillValue, { shouldDirty = true } = {}) => {
      markWritten(value);
      setValue(field, value, { shouldDirty });
    },
    [markWritten, setValue],
  );

  const isManuallyEdited = useCallback(() => {
    const value = getValues(field);
    return !isBlank(value) && isUserValue(value);
  }, [getValues, isUserValue]);

  const prefill = useCallback(
    <R>({
      key,
      load,
      apply,
      errorMessage,
      delayMs = 0,
    }: CashbackPrefillRequest<R>) => {
      const before = getValues(field);
      let cancelled = false;
      setPending({ key, value: before });
      const timer = setTimeout(async () => {
        try {
          const result = await load();
          if (!cancelled && getValues(field) === before) {
            apply(result);
          }
        } catch {
          if (!cancelled) {
            setError(errorMessage);
          }
        } finally {
          if (!cancelled) {
            setPending(null);
          }
        }
      }, delayMs);
      return () => {
        cancelled = true;
        clearTimeout(timer);
        setPending(null);
      };
    },
    [getValues],
  );

  const isPending = (key: string, value: CashbackPrefillValue) =>
    pending?.key === key && value === pending.value;

  return {
    error,
    clearError,
    isUnchangedEdit,
    isManuallyEdited,
    isPending,
    write,
    prefill,
  };
};
